import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder } from '@ngneat/reactive-forms';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Roles } from '@utils/roles';
import { isNil } from 'lodash';
import { filter, map, Observable, Subject, takeUntil, tap } from 'rxjs';
import { getFailureMessageRxOp } from '@geekbears/gb-ngrx-stack-base';
import { ModalService, NotificationsService } from '@geekbears/ngx-gb-ui-kit';

import {
  removeAndAssignSuccessAction,
  removeMemberSuccessAction,
} from '@modules/company-manager/clients/application';
import {
  getUserDetailsFailureAction,
  updateUserFailureAction,
  updateUserSuccessAction,
  UsersActions,
  updateUserLoadingSelector,
  userDetailsLoadingSelector,
  userDetailsSuccessSelector,
} from '@modules/company-manager/users/application';

import {
  ProfileActions,
  updatePhotoLoadingSelector,
} from '@modules/regular-user/profile/application';
import { UsersModals } from '@modules/shared/presentation';
import { AppUser } from '@modules/company-manager/users/domain';

@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss'],
})
export class UserDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  formDisabled: boolean = true;
  user$: Observable<AppUser>;
  userLoading$: Observable<boolean | undefined>;
  editable: boolean = false;
  form!: FormGroup;
  userId: string = '';
  profilePhoto!: string;
  updateUserLoading$: Observable<boolean | any>;
  destroy$: Subject<void> = new Subject();
  uploadPhotoLoading$: Observable<boolean | any>;
  userDetails!: AppUser;
  isDisabled: boolean = false;
  constructor(
    private modalService: ModalService,
    private activatedRoute: ActivatedRoute,
    private store$: Store,
    private fb: FormBuilder,
    private notificationService: NotificationsService,
    private actions$: Actions,
    private router: Router
  ) {
    this.uploadPhotoLoading$ = this.store$.select(updatePhotoLoadingSelector);
    this.updateUserLoading$ = this.store$
      .select(updateUserLoadingSelector)
      .pipe(
        tap((status) => {
          if (status) this.isDisabled = true;
        })
      );
    this.userLoading$ = this.store$.select(userDetailsLoadingSelector);
    this.form = this.fb.group({
      email: [
        '',
        [
          Validators.required,
          Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'),
        ],
      ],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      jobTitle: ['', Validators.required],
      department: ['', Validators.required],
      role: ['', Validators.required],
    });
    this.user$ = this.store$.select(userDetailsSuccessSelector).pipe(
      filter((data) => {
        return !isNil(data);
      }),
      map((user) => {
        return user!;
      }),
      tap((user) => {
        this.userDetails = user;
        this.profilePhoto = user?.profilePicture?.file || '';
        this.form.get('email')?.setValue(user.email);
        this.form.get('firstName')?.setValue(user.firstName);
        this.form.get('lastName')?.setValue(user.lastName);
        this.form.get('jobTitle')?.setValue(user.jobTitle);
        this.form.get('department')?.setValue(user.department);
        this.form.get('role')?.setValue(user.roles[0]);

        let stringfiedRoles: string = JSON.stringify(
          user.roles
        ).toLocaleLowerCase();

        if (
          stringfiedRoles.includes('approver') &&
          stringfiedRoles.includes('requester')
        ) {
          this.form.get('role')?.setValue('approver');
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.onFailureActions();
    this.onUpdateUserSuccess();
    this.onUserDeleted();
    this.getParams();
  }
  OpenModal() {
    this.modalService.open(UsersModals, {
      data: { type: 'deleteUser', id: this.userDetails.id },
    });
  }
  ngAfterViewInit(): void {}
  onUpdateUserSuccess() {
    this.actions$
      .pipe(
        ofType(updateUserSuccessAction),
        filter((action) => {
          return !isNil(action);
        }),
        map((action) => {
          setTimeout(() => {
            this.isDisabled = false;
          }, 1500);
          this.notificationService.success('User successfully updated', {
            duration: 3000,
            icon: 'check-circle',
          });

          return action;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }
  getParams() {
    this.activatedRoute.params
      .pipe(map((params) => params))
      .subscribe(async (params) => {
        if (params['id']) this.userId = params['id'];
        this.store$.dispatch(
          UsersActions.getUserDetails({
            userId: params['id'],
          })
        );
      })
      .unsubscribe();
  }

  edit() {
    if (this.formDisabled) {
      this.formDisabled = !this.formDisabled;
    } else {
      this.updateUser();
    }
  }
  updateUser() {
    if (this.form.valid && this.isDisabled == false) {
      //if approver
      if (this.form.get('role')?.value == 'approver')
        this.form.get('role')?.setValue(['approver']);
      //if requester
      if (this.form.get('role')?.value == 'requester')
        this.form.get('role')?.setValue(['requester']);

      this.store$.dispatch(
        UsersActions.updateUser({
          userData: this.form.value,
          userId: this.userId,
        })
      );
    }
  }
  onFailureActions() {
    this.actions$
      .pipe(
        ofType(updateUserFailureAction, getUserDetailsFailureAction),
        map((action) => {
          return action.failure;
        }),
        getFailureMessageRxOp(),
        tap((errorMessage) => {
          setTimeout(() => {
            this.isDisabled = false;
          }, 1500);
          if (errorMessage) {
            this.notificationService.error(errorMessage, {
              duration: 5000,
              icon: 'alert-outline',
            });
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }
  onFileSelected(event: any) {
    if (event.target.files && event.target.files[0]) {
      var reader = new FileReader();
      let m: any = this;
      reader.onload = function (e: any) {
        m.profilePhoto = e.target.result;
      };
      reader.readAsDataURL(event.target.files[0]);
      this.store$.dispatch(
        ProfileActions.updatePhoto({
          photo: event.target.files[0],
        })
      );
    }
  }

  onUserDeleted() {
    this.actions$
      .pipe(
        ofType(removeAndAssignSuccessAction, removeMemberSuccessAction),
        filter((action: any) => {
          return !isNil(action);
        }),
        map((action: any) => {
          this.router.navigateByUrl('dashboard/users');
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }
  isCompanyManager(roles: string[]) {
    const foundRole = roles.filter(
      (role) => role == Roles.companyAdministrator
    );
    if (foundRole.length == 0) return true;
    else return false;
  }
}
